import React, { useState, useEffect, useCallback } from "react";

export interface DocumentData {
  uu_id: string;
  document_type: string;
  document_uuid: string;
  document_path: string;
  created_at: string;
  updated_at: string;
  reporter?: string;
}

export interface DocumentOnlyViewerProps {
  file: DocumentData;
  baseUrl?: string;
  className?: string;
  showMetadata?: boolean;
  onDownload?: (documentData: DocumentData) => void;
  actionTrigger: React.ReactNode;
}

const DocumentOnlyViewer: React.FC<DocumentOnlyViewerProps> = ({
  file,
  baseUrl = "",
  className = "",
  showMetadata = true,
  onDownload,
  actionTrigger,
}) => {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<string>("");

  // Construct file URL and type on mount or when document changes
  useEffect(() => {
    if (!file) return;
    let url = "";
    if (baseUrl) {
      const cleanBaseUrl = baseUrl.replace(/\/$/, "");
      url = `${cleanBaseUrl}/${file.document_uuid}`;
    } else if (file.document_path.startsWith("http")) {
      url = file.document_path;
    }
    setFileUrl(url);
    const ext = file.document_path.split(".").pop()?.toLowerCase() || "";
    setFileType(getFileType(ext));
  }, [file, baseUrl]);

  const getFileType = (extension: string): string => {
    const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const videoTypes = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
    const audioTypes = ["mp3", "wav", "ogg", "aac", "flac"];
    const pdfTypes = ["pdf"];
    const documentTypes = ["doc", "docx", "txt", "rtf"];
    const spreadsheetTypes = ["xls", "xlsx", "csv"];

    if (imageTypes.includes(extension)) return "image";
    if (videoTypes.includes(extension)) return "video";
    if (audioTypes.includes(extension)) return "audio";
    if (pdfTypes.includes(extension)) return "pdf";
    if (documentTypes.includes(extension)) return "document";
    if (spreadsheetTypes.includes(extension)) return "spreadsheet";

    return "other";
  };

  // Fetch any file as blob and create blob URL (with auth)
  const fetchFileAsBlob = async (fileUrl: string) => {
    try {
      setLoading(true);
      setError("");
      const token = await sessionStorage.getItem("authToken");
      const headers: HeadersInit = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(fileUrl, {
        credentials: "include",
        headers,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Empty file received");
      }
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setLoading(false);
      return url;
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      setError(`Failed to load file: ${errorMessage}`);
      return null;
    }
  };

  const handleDownload = async () => {
    try {
      if (onDownload) onDownload(file);
      if (!fileUrl) {
        setError("No file URL available for download");
        return;
      }
      // Always fetch as blob for download (with auth)
      let url = blobUrl;
      if (!url) {
        url = (await fetchFileAsBlob(fileUrl)) || "";
        if (!url) return;
      }
      const link = window.document.createElement("a");
      link.href = url;
      link.download =
        file.document_path.split("/").pop() || `document-${file.document_uuid}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (err) {
      setError("Failed to download file");
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case "image":
        return "🖼️";
      case "pdf":
        return "📄";
      case "video":
        return "🎥";
      case "audio":
        return "🔊";
      case "document":
        return "📝";
      case "spreadsheet":
        return "📊";
      default:
        return "📎";
    }
  };

  const formatDocumentType = (docType: string | undefined): string => {
    if (!docType) return "Unknown Document";
    if (docType === "meeting") return "Minute";
    return docType.replace(/_/g, " ");
  };

  const openModal = async () => {
    if (fileType === "pdf" && fileUrl && !blobUrl) {
      setLoading(true);
      await fetchFileAsBlob(fileUrl);
      setLoading(false);
      setIsModalOpen(true);
    } else {
      setIsModalOpen(true);
      if (fileUrl && !blobUrl) {
        await fetchFileAsBlob(fileUrl);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Clean up blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Render modal content based on file type
  const renderModalContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          Loading document...
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-red-700 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="text-2xl mb-2">❌</div>
          <div className="text-center">
            <div className="font-medium mb-2">Error loading document</div>
            <div className="text-sm mb-4">{error}</div>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Try Download Anyway
            </button>
          </div>
        </div>
      );
    }
    switch (fileType) {
      case "image":
        return (
          <div className="flex justify-center bg-black bg-opacity-5 rounded-lg p-4">
            <img
              src={blobUrl || fileUrl}
              alt={`Document ${file.document_uuid}`}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
              onError={() => setError("Failed to load image")}
            />
          </div>
        );
      case "pdf":
        return blobUrl ? (
          <div
            className="flex justify-center bg-gray-100 rounded-lg p-4"
            style={{ height: "70vh" }}
          >
            <iframe
              src={blobUrl}
              title="PDF Preview"
              className="w-full h-full rounded-lg shadow-sm border"
              style={{ minHeight: "60vh", minWidth: "60vw" }}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="text-4xl mb-4">📄</div>
            <div>PDF preview not available.</div>
            <button
              onClick={handleDownload}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Download PDF
            </button>
          </div>
        );
      case "video":
        return (
          <div className="flex justify-center bg-black rounded-lg p-4">
            <video
              controls
              src={blobUrl || fileUrl}
              className="max-w-full max-h-[70vh]"
              onError={() => setError("Failed to load video")}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      case "audio":
        return (
          <div className="flex justify-center bg-gray-100 rounded-lg p-8">
            <audio
              controls
              src={blobUrl || fileUrl}
              className="w-full max-w-md"
              onError={() => setError("Failed to load audio")}
            >
              Your browser does not support the audio tag.
            </audio>
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="text-4xl mb-4">{getFileIcon(fileType)}</div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {fileType.charAt(0).toUpperCase() + fileType.slice(1)} File
            </h4>
            <p className="text-gray-600 mb-4 max-w-md">
              This file type cannot be previewed in the browser. Please download
              the file to view it.
            </p>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <span>📥</span>
              Download File
            </button>
          </div>
        );
    }
  };

  return (
    <div className={className}>
      <span
        onClick={openModal}
        style={{ display: "inline-block", cursor: "pointer" }}
      >
        {actionTrigger}
      </span>
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="text-2xl">{getFileIcon(fileType)}</div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 capitalize">
                    {formatDocumentType(file.document_type)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {file.document_path.split("/").pop()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  disabled={loading || !!error || !fileUrl}
                  className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${
                        loading || error || !fileUrl
                          ? "bg-gray-300 cursor-not-allowed text-gray-500"
                          : "bg-green-600 hover:bg-green-700 text-white shadow-sm focus:ring-green-500"
                      }
                    `}
                >
                  <span>📥</span>
                  Download
                </button>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {renderModalContent()}
            </div>

            {/* Modal Footer */}
            {showMetadata && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Created:</span>
                    <p className="text-gray-600">
                      {new Date(file.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Updated:</span>
                    <p className="text-gray-600">
                      {new Date(file.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <p className="text-gray-600 capitalize">
                      {formatDocumentType(file.document_type)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentOnlyViewer;
